import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Box, Typography, TextField, InputAdornment, Select, MenuItem, FormControl, InputLabel, Button, Pagination, Alert, CircularProgress, ToggleButton, ToggleButtonGroup, IconButton, Chip, Snackbar } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import RefreshIcon from '@mui/icons-material/Refresh';
import ClearIcon from '@mui/icons-material/Clear';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import { useUser } from '../contexts/UserContext';
import { useGuidanceStudents } from '../contexts/GuidanceStudentsContext';
import StudentList, { StudentListItem } from '../components/students/StudentList';
import { getDatabase, ref, update } from 'firebase/database';

const Students: React.FC = () => {
  const { user } = useUser();
  const { students, loading, error, refreshStudents } = useGuidanceStudents();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const database = getDatabase();
  
  // Check if navigating from sidebar (fresh navigation) - reset filters
  const isFromSidebar = location.state?.fromSidebar === true;
  
  // Snackbar state for notifications
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  
  // Initialize state from URL params, default filter, or defaults
  const getInitialState = () => {
    // Check if URL params exist (highest priority)
    const hasUrlParams = searchParams.get('lastNameStart') || searchParams.get('lastNameEnd') || 
                         searchParams.get('filterGrade') || searchParams.get('searchName') || 
                         searchParams.get('searchFavoriteSchools') || searchParams.get('filterFavoritePrograms');
    
    // If coming from sidebar, use default filter if available, otherwise use defaults
    if (isFromSidebar) {
      const defaultFilter = user?.isGuidanceUser ? user?.defaultStudentFilter : undefined;
      return {
        viewMode: 'list' as 'list' | 'card',
        showAlumni: false,
        searchName: '',
        searchFavoriteSchools: '',
        filterGrade: defaultFilter?.grades || [] as string[],
        filterFavoritePrograms: [] as string[],
        lastNameRangeStart: defaultFilter?.lastNameRange?.start || '',
        lastNameRangeEnd: defaultFilter?.lastNameRange?.end || '',
        page: 1,
      };
    }
    
    // If URL params exist, use them (override default filter)
    if (hasUrlParams) {
      return {
        viewMode: (searchParams.get('viewMode') || 'list') as 'list' | 'card',
        showAlumni: searchParams.get('showAlumni') === 'true',
        searchName: searchParams.get('searchName') || '',
        searchFavoriteSchools: searchParams.get('searchFavoriteSchools') || '',
        filterGrade: searchParams.get('filterGrade')?.split(',').filter(Boolean) || [] as string[],
        filterFavoritePrograms: searchParams.get('filterFavoritePrograms')?.split(',').filter(Boolean) || [] as string[],
        lastNameRangeStart: searchParams.get('lastNameStart') || '',
        lastNameRangeEnd: searchParams.get('lastNameEnd') || '',
        page: parseInt(searchParams.get('page') || '1', 10),
      };
    }
    
    // No URL params - use default filter if available
    const defaultFilter = user?.isGuidanceUser ? user?.defaultStudentFilter : undefined;
    return {
      viewMode: (searchParams.get('viewMode') || 'list') as 'list' | 'card',
      showAlumni: searchParams.get('showAlumni') === 'true',
      searchName: '',
      searchFavoriteSchools: '',
      filterGrade: defaultFilter?.grades || [] as string[],
      filterFavoritePrograms: [] as string[],
      lastNameRangeStart: defaultFilter?.lastNameRange?.start || '',
      lastNameRangeEnd: defaultFilter?.lastNameRange?.end || '',
      page: 1,
    };
  };

  const initialState = getInitialState();
  const [viewMode, setViewMode] = useState<'list' | 'card'>(initialState.viewMode);
  const [showAlumni, setShowAlumni] = useState(initialState.showAlumni);
  const [searchName, setSearchName] = useState(initialState.searchName);
  const [searchFavoriteSchools, setSearchFavoriteSchools] = useState(initialState.searchFavoriteSchools);
  const [filterGrade, setFilterGrade] = useState<string[]>(initialState.filterGrade);
  const [filterFavoritePrograms, setFilterFavoritePrograms] = useState<string[]>(initialState.filterFavoritePrograms);
  const [lastNameRangeStart, setLastNameRangeStart] = useState<string>(initialState.lastNameRangeStart);
  const [lastNameRangeEnd, setLastNameRangeEnd] = useState<string>(initialState.lastNameRangeEnd);
  
  // Get current year and month (for graduation timing)
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1; // getMonth() returns 0-11, so add 1 for 1-12
  
  // Sorting state
  const [sortConfig, setSortConfig] = useState<{ key: 'name' | 'grade'; direction: 'asc' | 'desc' }>({
    key: 'name',
    direction: 'asc',
  });
  
  // Pagination state
  const [page, setPage] = useState(initialState.page);
  const itemsPerPage = 30;

  // Apply default filter when user data loads (if no URL params)
  useEffect(() => {
    if (user?.isGuidanceUser && user?.defaultStudentFilter && !isFromSidebar) {
      const hasUrlParams = searchParams.get('lastNameStart') || searchParams.get('lastNameEnd') || 
                           searchParams.get('filterGrade');
      if (!hasUrlParams) {
        // Apply default filter
        if (user.defaultStudentFilter.lastNameRange) {
          setLastNameRangeStart(user.defaultStudentFilter.lastNameRange.start || '');
          setLastNameRangeEnd(user.defaultStudentFilter.lastNameRange.end || '');
        }
        if (user.defaultStudentFilter.grades && user.defaultStudentFilter.grades.length > 0) {
          setFilterGrade(user.defaultStudentFilter.grades);
        }
      }
    }
  }, [user?.isGuidanceUser, user?.defaultStudentFilter, isFromSidebar, searchParams]);

  // Update URL params when filters change (but not when coming from sidebar initially)
  useEffect(() => {
    if (isFromSidebar) {
      // Clear the fromSidebar flag after first render
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }

    const params = new URLSearchParams();
    if (viewMode !== 'list') params.set('viewMode', viewMode);
    if (showAlumni) params.set('showAlumni', 'true');
    if (searchName) params.set('searchName', searchName);
    if (searchFavoriteSchools) params.set('searchFavoriteSchools', searchFavoriteSchools);
    if (filterGrade.length > 0) params.set('filterGrade', filterGrade.join(','));
    if (filterFavoritePrograms.length > 0) params.set('filterFavoritePrograms', filterFavoritePrograms.join(','));
    if (lastNameRangeStart) params.set('lastNameStart', lastNameRangeStart);
    if (lastNameRangeEnd) params.set('lastNameEnd', lastNameRangeEnd);
    if (page > 1) params.set('page', page.toString());
    // Preserve fromGuidance parameter if present
    if (searchParams.get('fromGuidance') === '1') {
      params.set('fromGuidance', '1');
    }

    setSearchParams(params, { replace: true });
  }, [viewMode, showAlumni, searchName, searchFavoriteSchools, filterGrade, filterFavoritePrograms, lastNameRangeStart, lastNameRangeEnd, page, isFromSidebar, navigate, location.pathname, setSearchParams, searchParams]);

  // Clear all filters function (preserves Current/Alumni toggle)
  const clearAllFilters = () => {
    // Preserve Current/Alumni toggle state
    const currentShowAlumni = showAlumni;
    
    // Clear all filter inputs
    setSearchName('');
    setSearchFavoriteSchools('');
    setFilterGrade([]);
    setFilterFavoritePrograms([]);
    setLastNameRangeStart('');
    setLastNameRangeEnd('');
    setPage(1);
    
    // Note: showAlumni state is NOT changed - it preserves the toggle
    
    // Clear URL params but preserve fromGuidance and showAlumni if needed
    const params = new URLSearchParams();
    if (searchParams.get('fromGuidance') === '1') {
      params.set('fromGuidance', '1');
    }
    if (currentShowAlumni) {
      params.set('showAlumni', 'true');
    }
    setSearchParams(params, { replace: true });
  };

  // Save default filter to Firebase
  const saveDefaultFilter = async () => {
    if (!user?.isGuidanceUser || !user?.uid) {
      setSnackbar({ open: true, message: 'Only guidance users can save default filters', severity: 'error' });
      return;
    }

    try {
      const updates: { [key: string]: any } = {
        [`highSchoolUser/${user.uid}/defaultStudentFilter`]: {
          lastNameRange: lastNameRangeStart && lastNameRangeEnd ? {
            start: lastNameRangeStart.toUpperCase().trim(),
            end: lastNameRangeEnd.toUpperCase().trim()
          } : null,
          grades: filterGrade.length > 0 ? filterGrade : null
        }
      };

      await update(ref(database), updates);
      setSnackbar({ open: true, message: 'Default filter saved successfully', severity: 'success' });
    } catch (error: any) {
      console.error('Error saving default filter:', error);
      setSnackbar({ open: true, message: 'Failed to save default filter', severity: 'error' });
    }
  };

  // Clear default filter from Firebase
  const clearDefaultFilter = async () => {
    if (!user?.isGuidanceUser || !user?.uid) {
      setSnackbar({ open: true, message: 'Only guidance users can clear default filters', severity: 'error' });
      return;
    }

    try {
      const updates: { [key: string]: any } = {
        [`highSchoolUser/${user.uid}/defaultStudentFilter`]: null
      };

      await update(ref(database), updates);
      // Clear local filter inputs
      setLastNameRangeStart('');
      setLastNameRangeEnd('');
      setFilterGrade([]);
      setSnackbar({ open: true, message: 'Default filter cleared', severity: 'success' });
    } catch (error: any) {
      console.error('Error clearing default filter:', error);
      setSnackbar({ open: true, message: 'Failed to clear default filter', severity: 'error' });
    }
  };

  // Check if current filter matches saved default
  const isDefaultFilterActive = useMemo(() => {
    if (!user?.isGuidanceUser || !user?.defaultStudentFilter) return false;
    
    const defaultFilter = user.defaultStudentFilter;
    const lastNameMatch = (!defaultFilter.lastNameRange && !lastNameRangeStart && !lastNameRangeEnd) ||
      (defaultFilter.lastNameRange?.start === lastNameRangeStart.toUpperCase() &&
       defaultFilter.lastNameRange?.end === lastNameRangeEnd.toUpperCase());
    const gradesMatch = (!defaultFilter.grades && filterGrade.length === 0) ||
      (defaultFilter.grades && JSON.stringify([...defaultFilter.grades].sort()) === JSON.stringify([...filterGrade].sort()));
    
    return lastNameMatch && gradesMatch;
  }, [user?.defaultStudentFilter, lastNameRangeStart, lastNameRangeEnd, filterGrade]);

  // Helper function to check if a student matches Current/Alumni filter
  const matchesCurrentAlumniFilter = useMemo(() => {
    return (student: StudentListItem): boolean => {
      const classYear = parseInt(student.grade, 10);
      if (isNaN(classYear)) return false;
      
      if (showAlumni) {
        if (currentMonth <= 6) {
          return classYear < currentYear;
        } else {
          return classYear <= currentYear;
        }
      } else {
        if (currentMonth <= 6) {
          return classYear >= currentYear;
        } else {
          return classYear > currentYear;
        }
      }
    };
  }, [showAlumni, currentYear, currentMonth]);

  // Get unique values for filters (filtered by Current/Alumni selection)
  const uniqueGrades = useMemo(() => {
    const filtered = students.filter(matchesCurrentAlumniFilter);
    const grades = new Set(filtered.map(s => s.grade).filter(Boolean));
    return Array.from(grades).sort();
  }, [students, matchesCurrentAlumniFilter]);


  const uniqueProgramsWithSchools = useMemo(() => {
    const filtered = students.filter(matchesCurrentAlumniFilter);
    const programs = new Set<string>();
    filtered.forEach(s => {
      if (s.favoriteProgramsWithSchools) {
        s.favoriteProgramsWithSchools.forEach(program => programs.add(program));
      }
    });
    return Array.from(programs).sort();
  }, [students, matchesCurrentAlumniFilter]);

  // Filter and sort students
  const filteredAndSortedStudents = useMemo(() => {
    let filtered = [...students];

    // Filter out users with AccountType 2 (Parent), 3 (Guidance), or 4 (Other)
    // Only show AccountType 1 (Student) or undefined (defaults to student)
    filtered = filtered.filter(s => {
      const accountType = s.accountType;
      // Convert to number if it's a string (handle both string and number formats)
      const accountTypeNum = accountType !== undefined && accountType !== null
        ? (typeof accountType === 'string' ? parseInt(accountType, 10) : accountType)
        : undefined;
      // Include if accountType is undefined, null, or 1 (Student)
      // Exclude if accountType is 2 (Parent), 3 (Guidance), or 4 (Other)
      return accountTypeNum === undefined || accountTypeNum === null || accountTypeNum === 1;
    });

    // Current/Alumni filter (applied first)
    // Students graduate in June, so:
    // - Before/During June (month <= 6): Class Of current year hasn't graduated yet
    // - After June (month > 6): Class Of current year has already graduated
    filtered = filtered.filter(s => {
      const classYear = parseInt(s.grade, 10);
      if (isNaN(classYear)) return false; // Exclude students without valid class year
      
      if (showAlumni) {
        // Show Alumni
        if (currentMonth <= 6) {
          // Before/during June: Alumni = Class Of < current year
          return classYear < currentYear;
        } else {
          // After June: Alumni = Class Of <= current year (includes this year's graduates)
          return classYear <= currentYear;
        }
      } else {
        // Show Current
        if (currentMonth <= 6) {
          // Before/during June: Current = Class Of >= current year (includes this year's seniors)
          return classYear >= currentYear;
        } else {
          // After June: Current = Class Of > current year (excludes this year's graduates)
          return classYear > currentYear;
        }
      }
    });

    // Name search
    if (searchName.trim()) {
      const searchLower = searchName.toLowerCase().trim();
      filtered = filtered.filter(s => {
        const fullName = `${s.lastName}, ${s.firstName}`.toLowerCase();
        const firstName = s.firstName.toLowerCase();
        const lastName = s.lastName.toLowerCase();
        return fullName.includes(searchLower) || firstName.includes(searchLower) || lastName.includes(searchLower);
      });
    }

    // Favorite Schools search
    if (searchFavoriteSchools.trim()) {
      const searchLower = searchFavoriteSchools.toLowerCase().trim();
      filtered = filtered.filter(s => {
        return s.favoriteSchools.some(school => school.toLowerCase().includes(searchLower));
      });
    }

    // Last Name Range filter (supports 1-3 character prefixes)
    if (lastNameRangeStart && lastNameRangeEnd) {
      filtered = filtered.filter(s => {
        const studentLastName = s.lastName.toUpperCase();
        const startUpper = lastNameRangeStart.toUpperCase();
        const endUpper = lastNameRangeEnd.toUpperCase();
        
        // Get the prefix length to compare (use the longer of start/end)
        const prefixLength = Math.max(startUpper.length, endUpper.length);
        const studentPrefix = studentLastName.substring(0, prefixLength);
        
        // Compare: student prefix must be >= start and <= end
        return studentPrefix >= startUpper && studentPrefix <= endUpper;
      });
    }

    // Grade filter
    if (filterGrade.length > 0) {
      filtered = filtered.filter(s => filterGrade.includes(s.grade));
    }

    // Favorite Programs filter (by formatted program name with school)
    if (filterFavoritePrograms.length > 0) {
      filtered = filtered.filter(s => {
        if (!s.favoriteProgramsWithSchools || s.favoriteProgramsWithSchools.length === 0) {
          return false;
        }
        return filterFavoritePrograms.some(selectedProgram => 
          s.favoriteProgramsWithSchools.includes(selectedProgram)
        );
      });
    }

    // Sort
    filtered.sort((a, b) => {
      let aValue: string;
      let bValue: string;

      if (sortConfig.key === 'name') {
        aValue = `${a.lastName}, ${a.firstName}`.toLowerCase();
        bValue = `${b.lastName}, ${b.firstName}`.toLowerCase();
      } else {
        aValue = a.grade || '';
        bValue = b.grade || '';
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [students, searchName, searchFavoriteSchools, lastNameRangeStart, lastNameRangeEnd, filterGrade, filterFavoritePrograms, sortConfig, showAlumni, currentYear, currentMonth]);

  // Paginated students
  const paginatedStudents = useMemo(() => {
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredAndSortedStudents.slice(startIndex, endIndex);
  }, [filteredAndSortedStudents, page, itemsPerPage]);

  // Reset page and clear filters when Current/Alumni toggle changes
  useEffect(() => {
    setPage(1);
    // Clear filters that may no longer be valid after switching Current/Alumni
    setFilterGrade([]);
    setFilterFavoritePrograms([]);
  }, [showAlumni]);

  // Reset page when other filters change
  useEffect(() => {
    setPage(1);
  }, [searchName, searchFavoriteSchools, lastNameRangeStart, lastNameRangeEnd, filterGrade, filterFavoritePrograms]);

  const totalPages = Math.ceil(filteredAndSortedStudents.length / itemsPerPage);

  const handleSort = (key: 'name' | 'grade') => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={refreshStudents}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      </Box>
    );
  }

  // Check if navigating from Guidance Dashboard
  const fromGuidance = searchParams.get('fromGuidance') === '1';

  return (
    <Box sx={{ p: 2 }}>
      {/* Back Button */}
      {fromGuidance && (
        <Button 
          variant="outlined" 
          onClick={() => {
            navigate('/guidance-dashboard');
          }} 
          sx={{ mb: 3 }}
        >
          &larr; Back
        </Button>
      )}
      <Box sx={{ 
        display: 'flex', 
        flexWrap: 'wrap',
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 3,
        gap: 2
      }}>
        <Typography 
          variant="h4"
          sx={{
            whiteSpace: 'nowrap',
            flex: '0 1 auto',
            minWidth: 0
          }}
        >
          {user?.schoolName ? `${user.schoolName} Students` : 'Students'}
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          flexShrink: 0,
          flexWrap: 'wrap',
          minWidth: 'fit-content'
        }}>
          <ToggleButtonGroup
            value={showAlumni ? 'alumni' : 'current'}
            exclusive
            onChange={(_, newValue) => {
              if (newValue !== null) {
                setShowAlumni(newValue === 'alumni');
              }
            }}
            size="small"
            sx={{ mr: 1 }}
          >
            <ToggleButton value="current">
              Show Current
            </ToggleButton>
            <ToggleButton value="alumni">
              Show Alumni
            </ToggleButton>
          </ToggleButtonGroup>
          <IconButton onClick={refreshStudents} title="Refresh">
            <RefreshIcon />
          </IconButton>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, newMode) => newMode && setViewMode(newMode)}
            size="small"
          >
            <ToggleButton value="list">
              <ViewListIcon />
            </ToggleButton>
            <ToggleButton value="card">
              <ViewModuleIcon />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* Search and Filters */}
      <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', flex: 1 }}>
          <TextField
            placeholder="Search by Name..."
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            size="small"
            sx={{ flex: 1, minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            placeholder="Search by Favorite Schools..."
            value={searchFavoriteSchools}
            onChange={(e) => setSearchFavoriteSchools(e.target.value)}
            size="small"
            sx={{ flex: 1, minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          </Box>
          
          {/* Clear All Filters Button */}
          {(searchName || searchFavoriteSchools || filterGrade.length > 0 || filterFavoritePrograms.length > 0 || lastNameRangeStart || lastNameRangeEnd) && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<ClearIcon />}
              onClick={clearAllFilters}
              sx={{ flexShrink: 0 }}
            >
              Clear All
            </Button>
          )}
        </Box>

        {/* Default Filter Status Display */}
        {user?.isGuidanceUser && isDefaultFilterActive && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Chip 
              label="Default Filter Active" 
              color="primary" 
              size="small"
              sx={{ fontWeight: 500 }}
            />
            {(lastNameRangeStart || lastNameRangeEnd) && (
              <Typography variant="body2" color="text.secondary">
                Last Name: {lastNameRangeStart.toUpperCase()}-{lastNameRangeEnd.toUpperCase()}
              </Typography>
            )}
            {filterGrade.length > 0 && (
              <Typography variant="body2" color="text.secondary">
                Class of: {filterGrade.join(', ')}
              </Typography>
            )}
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          {/* Favourite Programs Filter */}
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>{`${user?.country === 'US' ? 'Favorite' : 'Favourite'} Programs`}</InputLabel>
            <Select
              multiple
              value={filterFavoritePrograms}
              onChange={(e) => setFilterFavoritePrograms(typeof e.target.value === 'string' ? [e.target.value] : e.target.value)}
              label={`${user?.country === 'US' ? 'Favorite' : 'Favourite'} Programs`}
              renderValue={(selected) => selected.length === 0 ? 'All' : `${selected.length} selected`}
            >
              {uniqueProgramsWithSchools.map((program) => (
                <MenuItem key={program} value={program}>
                  {program}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Last Name Range Filter (for guidance users) */}
          {user?.isGuidanceUser && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                label="Last Name Start"
                value={lastNameRangeStart}
                onChange={(e) => {
                  const value = e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 3);
                  setLastNameRangeStart(value);
                }}
                size="small"
                sx={{ width: 120 }}
                inputProps={{ maxLength: 3 }}
                helperText="1-3 letters"
              />
              <Typography variant="body2" sx={{ mt: 2 }}>to</Typography>
              <TextField
                label="Last Name End"
                value={lastNameRangeEnd}
                onChange={(e) => {
                  const value = e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 3);
                  setLastNameRangeEnd(value);
                }}
                size="small"
                sx={{ width: 120 }}
                inputProps={{ maxLength: 3 }}
                helperText="1-3 letters"
              />
            </Box>
          )}

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Class of</InputLabel>
            <Select
              multiple
              value={filterGrade}
              onChange={(e) => setFilterGrade(typeof e.target.value === 'string' ? [e.target.value] : e.target.value)}
              label="Class of"
              renderValue={(selected) => selected.length === 0 ? 'All' : `${selected.length} selected`}
            >
              {uniqueGrades.map((grade) => (
                <MenuItem key={grade} value={grade}>
                  {grade}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Set as Default button (for guidance users) - right after Class of */}
          {user?.isGuidanceUser && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<SaveIcon />}
              onClick={saveDefaultFilter}
              disabled={!lastNameRangeStart && !lastNameRangeEnd && filterGrade.length === 0}
            >
              Set as Default
            </Button>
          )}
        </Box>

        {/* Clear Default button (for guidance users) */}
        {user?.isGuidanceUser && isDefaultFilterActive && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              size="small"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={clearDefaultFilter}
            >
              Clear Default
            </Button>
          </Box>
        )}

        <Typography variant="body2" color="text.secondary">
          {filteredAndSortedStudents.length} student{filteredAndSortedStudents.length !== 1 ? 's' : ''} displayed
        </Typography>
      </Box>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Student List */}
      {paginatedStudents.length > 0 ? (
        <>
          {viewMode === 'list' && (
            <StudentList
              students={paginatedStudents}
              viewMode="list"
              onStudentClick={(studentId) => navigate(`/students/${studentId}`, { state: { fromStudents: true } })}
            />
          )}
          {viewMode === 'card' && (
            <StudentList
              students={paginatedStudents}
              viewMode="card"
              onStudentClick={(studentId) => navigate(`/students/${studentId}`, { state: { fromStudents: true } })}
            />
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, newPage) => setPage(newPage)}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      ) : (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary">
            No students found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your search or filters
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default Students;
